import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { PopupListItem, RecordData, Service } from 'webapp-types';
import { GenieBuildBoardEntryPoint } from 'webapp/enums/ai-chat';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { arrayMove, sortArrayBasedOnSubset, sortBy } from 'webapp/utils/common';
import {
	clearTableCellWidthsOnDragStop,
	getSortableContainment,
	getSortableItemIndex,
	setTableCellWidthsOnDragStart,
} from 'webapp/utils/html';

import { containerEventName } from 'webapp/modifiers/visible-check-container';

import type {
	AddTagAction,
	DeleteAction,
	DeletePermanentlyAction,
	RestoreAction,
	ToggleFavoriteAction,
} from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import type Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import { BoardCategory, BoardFilterId } from 'webapp/models-custom/filter-sort/board';
import type FilterSortBoardInGrid from 'webapp/models-custom/filter-sort/board-in-grid';
import type FilterSortBoardInList from 'webapp/models-custom/filter-sort/board-in-list';
import Report from 'webapp/models-custom/report';
import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';

import type { TrackingOriginTypeForPreview } from 'webapp/services/model-manager/boards';

export interface Args {
	model: Board[];
	transferedBoardPublicTemplateNotifications: ResolutionCenterItem[];
	transferedBoardAccountTemplateNotifications: ResolutionCenterItem[];
	hasBoards: boolean;
	onBoardCreateWizard: (tagId?: number) => void;
	onBoardCreateChoice: () => void;
	onOpenView: (viewId: string) => void;
	onBoardCreate: (tagId?: number) => void;
	onCopy: (boardId: string) => void;
	updateIsRecentlyDeletedFilterActive: (isActive: boolean) => void;
}

type Action =
	| 'batchAddToLoop'
	| 'batchAddToReport'
	| ToggleFavoriteAction
	| 'batchCopyToSpace'
	| 'batchToggleMobileVisibility'
	| DeletePermanentlyAction
	| AddTagAction
	| DeleteAction
	| RestoreAction;

type AddButtonAction =
	'createBoard' | 'createBoardWizard' | 'useAccountTemplate' | 'usePublicTemplate';

type AddButtonOption = PopupListItem<AddButtonAction>;

export default class BoardsIndexBaseComponent<T extends Args> extends Component<T> {
	@service('store') declare storeService: Service.Store;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	declare filterSort: FilterSortBoardInGrid | FilterSortBoardInList;

	visibleCheckContainerNode: HTMLElement | null = null;
	sortableElements: JQuery[] = [];
	addButtonText = 'New dashboard';
	batchSelect: BatchSelect<Board>;
	createEntryPoint = GenieBuildBoardEntryPoint.CREATE_WITH_AI;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as DeleteAction);
			},
		});

		void this.mmNotificationsService.requestNotificationsForFeature('board');
	}

	get noResults(): boolean {
		return (
			!this.args.hasBoards ||
			(this.filterSort.results.length === 0 &&
				this.args.transferedBoardAccountTemplateNotifications.length === 0 &&
				this.args.transferedBoardPublicTemplateNotifications.length === 0)
		);
	}

	get sortedModel(): Board[] {
		return sortBy(this.args.model, 'order', 'numericId');
	}

	get addBtnShown(): boolean {
		return (
			!this.currentUserService.isViewer &&
			(this.args.hasBoards || this.accountService.isClient) &&
			!this.isRecentlyDeletedActive
		);
	}

	get addButtonContainerNode() {
		return this.addBtnShown && !this.selectedItems.length
			? this.navigationService.addButtonContainerNode
			: null;
	}

	get batchButtonContainerNode() {
		return this.selectedItems.length ? this.navigationService.batchButtonContainerNode : null;
	}

	get generateWithAIButtonContainerNode() {
		return !this.batchButtonContainerNode
			? this.navigationService.generateWithAIButtonContainerNode
			: null;
	}

	get selectedItems(): Board[] {
		return this.batchSelect.selectedItems;
	}

	get areAllSelectedItemsFavorited(): boolean {
		return this.selectedItems.every((item) => item.favorite);
	}

	get areAllSelectedItemsHiddenOnMobile(): boolean {
		return this.selectedItems.every((item) => item.hidden);
	}

	get batchOptions(): PopupListItem<Action>[] {
		const selectedCount = this.selectedItems.length,
			allHiddenOnMobile = this.areAllSelectedItemsHiddenOnMobile,
			batchSelect = this.batchSelect,
			isAddToLoopDisabled = selectedCount > BoardLoop.maxBoardsInLoop,
			isAddToReportDisabled = selectedCount > Report.maxSlidesTypeBoard;

		let options: PopupListItem<Action>[] = [];

		if (this.filterSort.isRecentlyDeletedSelected) {
			options = [
				{
					icon: 'Trash',
					text: `Permanently delete (${selectedCount})`,
					action: 'batchDeletePermanently',
					theme: 'red',
				},
				{
					icon: 'Restore',
					text: `Restore (${selectedCount})`,
					action: 'batchRestore',
				},
			];

			return options;
		}

		if (!this.currentUserService.isViewer) {
			options = [
				{
					icon: 'Loop',
					text: `Add to loop (${selectedCount})`,
					action: 'batchAddToLoop',
					isDisabled: isAddToLoopDisabled,
					extra: isAddToLoopDisabled
						? {
								type: 'limitReached',
								text: `Maximum number of dashboards allowed in the loop is ${BoardLoop.maxBoardsInLoop}.`,
							}
						: undefined,
				},
				{
					icon: 'Reports',
					text: `Add to report (${selectedCount})`,
					action: 'batchAddToReport',
					isDisabled: isAddToReportDisabled,
					extra: isAddToReportDisabled
						? {
								type: 'limitReached',
								text: `Maximum number of dashboards allowed in the report is ${Report.maxSlidesTypeBoard}.`,
							}
						: undefined,
				},
			];
		}

		options.push(batchSelect.toggleFavoriteOption);

		if (this.currentUserService.showBoardTransfer) {
			options.push({
				icon: 'Share',
				text: `Copy to account (${selectedCount})`,
				action: 'batchCopyToSpace',
			});
		}

		options.push({
			icon: allHiddenOnMobile ? 'DeviceMobile' : 'MobileHidden',
			text: `${allHiddenOnMobile ? 'Make visible' : 'Hide'} on mobile (${selectedCount})`,
			action: 'batchToggleMobileVisibility',
		});

		if (!this.currentUserService.isViewer) {
			options = [...options, batchSelect.addTagsOption, batchSelect.deleteOption];
		}

		return options;
	}

	get addButtonOptions(): AddButtonOption[] {
		const options: AddButtonOption[] = [];

		options.push(
			{
				icon: 'Plus',
				text: 'New dashboard',
				action: 'createBoard',
			},
			{
				icon: 'Wizard',
				text: 'Use wizard',
				action: 'createBoardWizard',
			},
			{
				icon: 'FolderUser',
				text: 'Use account template',
				action: 'useAccountTemplate',
			},
		);

		if (this.accountService.showPublicTemplates) {
			options.push({
				icon: 'FolderOpen',
				text: 'Use public template',
				action: 'usePublicTemplate',
			});
		}

		return options;
	}

	get isRecentlyDeletedActive() {
		return this.filterSort.isRecentlyDeletedSelected;
	}

	get isRecentlyDeletedActiveWithResults() {
		return this.filterSort.isRecentlyDeletedSelected && this.filterSort.results.length > 0;
	}

	get isRecentlyDeletedLoading(): boolean {
		return (
			this.filterSort.results.length === 0 && this.mmBoardsService.isRecentlyDeletedLoading
		);
	}

	@cached
	get isNotificationsSectionVisible(): boolean {
		return (
			this.args.transferedBoardAccountTemplateNotifications.length > 0 ||
			this.args.transferedBoardPublicTemplateNotifications.length > 0
		);
	}

	@cached
	get isFavoriteSectionVisible(): boolean {
		return (
			this.favoriteBoardsFilterFn(this.filterSort.results).length > 0 &&
			!this.isNotificationsSectionVisible
		);
	}

	@cached
	get isNonFavoriteSectionVisible(): boolean {
		return !this.isFavoriteSectionVisible && !this.isNotificationsSectionVisible;
	}

	doBatchAction(actionName: DeleteAction) {
		return this[actionName]();
	}

	@action
	doAddButtonAction(actionName: string) {
		return this[actionName as AddButtonAction]();
	}

	initSortable(
		ctEl: JQuery,
		itemSelectorString: string,
		placeholderElementFn: (el: JQuery) => JQuery,
		sortingFavorites = false,
	): void {
		const containment = getSortableContainment(ctEl);

		ctEl.sortable({
			handle: '.drag',
			containment,
			placeholder: {
				element: placeholderElementFn,
				update: function () {
					return;
				},
			},
			forcePlaceholderSize: true,
			tolerance: 'pointer',
			items: `${itemSelectorString}:not(.notification):not(.placeholder)`,
			start: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				setTableCellWidthsOnDragStart(ui);

				const position = getSortableItemIndex(ui.item, itemSelectorString);
				ui.item.find('.drag').attr('data-position', position);
			},
			stop: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				clearTableCellWidthsOnDragStop(ui);

				const handle = ui.item.find('.drag'),
					oldIndex = Number(handle.attr('data-position')),
					newIndex = getSortableItemIndex(ui.item, itemSelectorString),
					id = handle.attr('data-id');

				if (oldIndex !== newIndex) {
					this.updateSortOrder(oldIndex, newIndex, sortingFavorites);
					this.analyticsService.event(TrackingEvent.DATABOARD_REORDER, {
						databoard_id: id,
						origin: 'home',
					});
				}
			},
		});

		this.sortableElements.push(ctEl);
	}

	destroySortable(): void {
		this.sortableElements.forEach((el) => {
			if (el?.length && el.sortable('instance')) {
				el.sortable('destroy');
			}
		});
		this.sortableElements = [];
	}

	updateSortOrder(oldIndex: number, newIndex: number, sortingFavorites: boolean): void {
		if (!sortingFavorites) {
			const favoriteItemsCount = this.sortedModel.filter((item) => item.favorite).length;
			oldIndex += favoriteItemsCount;
			newIndex += favoriteItemsCount;
		}

		const orderedIds = this.sortedModel.map((m) => m.numericId),
			filterOrderedIds = arrayMove(
				this.filterSort.results.map((m) => m.numericId),
				oldIndex,
				newIndex,
			),
			newSortOrder = sortArrayBasedOnSubset(orderedIds, filterOrderedIds);

		this.requestService
			.api<RecordData.Payload>('POST', '/boards/order', newSortOrder)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

		this.args.model.forEach((item) => {
			item.order = orderedIds.indexOf(item.numericId);
			item.applyBufferedChanges(['order']); // we won't be sending save
		});
	}

	transitionToTemplates(type: string) {
		this.navigationService.transitionToRoute(`boards.${type}-templates`);
	}

	@action
	batchAddToLoop() {
		return this.mmBoardsService.addToLoop(this.selectedItems);
	}

	@action
	batchAddToReport() {
		return this.mmBoardsService.openBatchAddToReportPopup(this.selectedItems);
	}

	@action
	batchToggleFavorite() {
		if (this.areAllSelectedItemsFavorited) {
			return this.mmBoardsService.batchUnfavorite(this.selectedItems);
		} else {
			return this.mmBoardsService.batchFavorite(this.selectedItems);
		}
	}

	@action
	batchCopyToSpace() {
		return this.mmBoardsService.openTransferPopup(this.selectedItems);
	}

	@action
	batchToggleMobileVisibility() {
		if (this.areAllSelectedItemsHiddenOnMobile) {
			return this.mmBoardsService.batchShowOnMobile(this.selectedItems);
		} else {
			return this.mmBoardsService.batchHideOnMobile(this.selectedItems);
		}
	}

	@action
	batchUpdateTags() {
		return this.mmBoardsService.batchUpdateTags(this.selectedItems);
	}

	@action
	batchDelete() {
		return this.mmBoardsService.batchDelete(this.selectedItems);
	}

	@action
	batchDeletePermanently() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;
		const remainingItemsAfterDelete =
			this.filterSort.results.length - this.selectedItems.length;

		return this.mmBoardsService.batchDeletePermanently(this.selectedItems).then((result) => {
			if (isRecentlyDeletedActive && remainingItemsAfterDelete === 0) {
				this.resetRecentlyDeletedFilter();
			}
			return result;
		});
	}

	@action
	deleteAllPermanently() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;

		return this.mmBoardsService
			.batchDeletePermanently(this.filterSort.results)
			.then((result) => {
				if (isRecentlyDeletedActive) {
					this.resetRecentlyDeletedFilter();
				}
				return result;
			});
	}

	@action
	batchRestore() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;
		const remainingItemsAfterRestore =
			this.filterSort.results.length - this.selectedItems.length;

		return this.mmBoardsService.batchRestore(this.selectedItems).then((result) => {
			if (isRecentlyDeletedActive && remainingItemsAfterRestore === 0) {
				this.resetRecentlyDeletedFilter();
			}
			return result;
		});
	}

	@action
	openPreview(board: Board, trackingOrigin?: string): void {
		this.mmBoardsService.openPreview(
			board,
			trackingOrigin as TrackingOriginTypeForPreview,
			this.filterSort.results,
		);
	}

	@action
	createBoard() {
		if (!this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			return;
		}

		const tagToAdd = this.filterSort.findFilter(2)?.id || undefined;
		this.args.onBoardCreate(tagToAdd);
	}

	@action
	createBoardWizard() {
		const tagToAdd = this.filterSort.findFilter(2)?.id || undefined;
		this.args.onBoardCreateWizard(tagToAdd);

		if (this.flowTourOnboardingService.isBannerShown) {
			this.flowTourOnboardingService.continueTour();
		}
	}

	@action
	useAccountTemplate() {
		this.transitionToTemplates('account');
	}

	@action
	usePublicTemplate() {
		this.transitionToTemplates('public');
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	favoriteBoardsFilterFn(items: Board[]): Board[] {
		return items.filter((item: Board) => item.favorite);
	}

	@action
	nonFavoriteBoardsFilterFn(items: Board[]): Board[] {
		return items.filter((item: Board) => !item.favorite);
	}

	resetRecentlyDeletedFilter(): void {
		const categoryFilter = this.filterSort.findFilter(BoardFilterId.CATEGORY);
		if (categoryFilter) {
			categoryFilter.selectOption(BoardCategory.ALL);
		}
	}

	@action
	handleSingleItemPermanentDelete(): void {
		if (this.isRecentlyDeletedActive && this.filterSort.results.length === 1) {
			this.resetRecentlyDeletedFilter();
		}
	}

	@action
	handleSingleItemRestore(): void {
		if (this.isRecentlyDeletedActive && this.filterSort.results.length === 1) {
			this.resetRecentlyDeletedFilter();
		}
	}
}
